import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@services/api';
import { useSessionStore } from '@stores/sessionStore';

interface DeviceRow {
  id: number;
  personalName?: string | null;
  deviceName?: string | null;
  displayName: string;
  trusted: boolean;
  isPrimary: boolean;
  isThisDevice: boolean;
  hasWhatsApp: boolean;
}

/**
 * Guest-facing "connected devices" manager. Any device can view + verify itself (OTP to the room
 * phone). A device that is itself verified (trusted) can rename other devices (slots) and mark them
 * verified so they can place orders / charge the folio — the guest-app equivalent of the staff
 * "trust device" action. (Strings are hardcoded English, like PhoneVerificationModal.)
 */
export default function ConnectedDevicesModal({ hotelSlug, onClose }: { hotelSlug: string; onClose: () => void }) {
  // Only the room's Primary (account owner) may verify/revoke or rename OTHER devices. Any device can
  // still verify and rename itself. isVerifierDevice() reflects DeviceRole.Primary (device_is_primary).
  const { isVerifierDevice } = useSessionStore();
  const callerIsPrimary = isVerifierDevice();

  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: { devices?: DeviceRow[] } }>(`/api/v1/${hotelSlug}/session/devices`);
      setDevices(res.data?.data?.devices ?? []);
    } catch {
      /* ignore — modal shows empty state */
    } finally {
      setLoading(false);
    }
  }, [hotelSlug]);

  useEffect(() => { load(); }, [load]);

  const toggleTrust = async (d: DeviceRow) => {
    setBusyId(d.id);
    try {
      await apiClient.post(`/api/v1/${hotelSlug}/session/devices/${d.id}/trust?trusted=${!d.trusted}`);
      await load();
    } catch {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (d: DeviceRow) => { setEditingId(d.id); setEditName(d.personalName || ''); };

  const saveName = async (d: DeviceRow) => {
    setBusyId(d.id);
    try {
      await apiClient.patch(`/api/v1/${hotelSlug}/session/devices/${d.id}/name`, { personalName: editName.trim() });
      setEditingId(null);
      await load();
    } catch {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  // Verify THIS device against the room phone via the existing OTP modal.
  const verifyThisDevice = () => {
    onClose();
    window.dispatchEvent(new Event('gcxp:verification-required'));
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📱 Connected Devices</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
            ) : devices.length === 0 ? (
              <p className="text-muted small mb-0">No devices yet.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {devices.map(d => (
                  <div key={d.id} className="border rounded p-2">
                    {editingId === d.id ? (
                      <div className="d-flex gap-1 align-items-center">
                        <input
                          className="form-control form-control-sm"
                          value={editName}
                          maxLength={50}
                          placeholder="e.g. Sarah, Dad"
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveName(d); }}
                          autoFocus
                        />
                        <button className="btn btn-sm btn-primary" disabled={busyId === d.id} onClick={() => saveName(d)}>✓</button>
                        <button className="btn btn-sm btn-light" onClick={() => setEditingId(null)}>✕</button>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="fw-medium text-truncate">{d.displayName}</span>
                          {d.isThisDevice && <span className="badge bg-info">This device</span>}
                          {d.isPrimary && <span className="badge bg-warning text-dark">⭐ Primary</span>}
                          {d.trusted
                            ? <span className="badge bg-success">✓ Verified</span>
                            : <span className="badge bg-secondary">Browse only</span>}
                        </div>
                        <div className="d-flex gap-2 mt-2 flex-wrap">
                          {(callerIsPrimary || d.isThisDevice) && (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(d)}>✏️ Rename</button>
                          )}
                          {d.isThisDevice && !d.trusted && (
                            <button className="btn btn-sm btn-primary" onClick={verifyThisDevice}>🔒 Verify this device</button>
                          )}
                          {callerIsPrimary && !d.isThisDevice && (
                            <button
                              className={`btn btn-sm ${d.trusted ? 'btn-outline-danger' : 'btn-success'}`}
                              disabled={busyId === d.id}
                              onClick={() => toggleTrust(d)}
                            >
                              {d.trusted ? 'Revoke' : '🔓 Make verified'}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-muted small mt-3 mb-0">
              {callerIsPrimary
                ? 'You are the primary device. You can verify or revoke other devices here so they can order and charge the room folio. Each device can also verify itself.'
                : 'Verify this device to place orders and charge the room folio. Only the room’s primary device can verify other devices.'}
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
